import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type {
  FlockFormInput,
  FlockRecord,
  PackingSlip,
  AddPackingSlipInput,
  Bird,
  AddFlockInput,
} from "../types/sentinel";
import { dateToISOString, isoToDateOnly } from "../utils/dateHelpers";

// API client instance - will be injected by components
let apiClient: any = null;

export const setSentinelApiClient = (client: any) => {
  apiClient = client;
};

// ===== STORE-TO-API TRANSFORMATIONS =====
/**
 * Transforms flock form data to API UpdateFlockInput format (camelCase).
 * Used by update() operation.
 */
const transformFlockToApiUpdate = (input: any, flockId: number) => {
  return {
    id: flockId,
    name: input.flockName || "",
    organizationId: input.flockOrganizationId,
    number: input.flockNumber || "",
    streetAddress: input.flockStreetAddress || "",
    city: input.flockCity || "",
    stateId: input.flockStateId,
    countyId: input.flockCountyId,
    zip: input.flockZip || "",
    latitude: input.flockLatitude || "",
    longitude: input.flockLongitude || "",
    isAddressValidated: input.flockIsAddressValidated || false,
    gpsNote: input.flockGpsNotes || "",
    active: input.flockActive !== false,
    primaryContactFirstName: input.flockPrimaryContactFirstName || "",
    primaryContactLastName: input.flockPrimaryContactLastName || "",
    primaryContactPhoneNumber: input.flockPrimaryContactPhoneNumber || "",
    primaryContactEmail: input.flockPrimaryContactEmail || "",
    secondaryContactFirstName: input.flockSecondaryContactFirstName || "",
    secondaryContactLastName: input.flockSecondaryContactLastName || "",
    secondaryContactPhoneNumber: input.flockSecondaryContactPhoneNumber || "",
    secondaryContactEmail: input.flockSecondaryContactEmail || "",
    comment: input.flockComment || "",
  };
};

/**
 * Transforms bird form data to API format for updates (camelCase).
 * Used by update() operation for existing birds.
 */
const transformBirdToApiUpdate = (bird: any) => {
  return {
    id: bird.birdId,
    birdNumber: bird.birdNumber || "",
    speciesId: bird.birdSpecies || 20, // Transform birdSpecies -> speciesId (API)
    ageInWeeks: bird.birdAgeInWeeks || 0,
    dateOfFirstBleed:
      dateToISOString(bird.birdDateFirstBleed) || new Date().toISOString(),
    dateTerminated: dateToISOString(bird.birdDateTerminated),
    note: bird.birdNote || "", // Transform birdNote -> note (API)
    active: bird.birdActive !== false,
  };
};

// ===== UNIFIED API-TO-STORE TRANSFORMATION =====
/**
 * Maps API response data to FlockRecord format (store format).
 * Used by both loadAll() for summary data and loadSiteDetails() for full data.
 *
 * @param apiSite - Raw site data from API (FlockSummary or Flock)
 * @param options - Optional configuration for transformation behavior
 * @returns FlockRecord in store format
 */
const mapApiFlockToRecord = (
  apiSite: any,
  options: { includeBirds?: boolean } = {}
): FlockRecord => {
  const { includeBirds = false } = options;

  return {
    flockId: apiSite.id,
    flockOrganizationId:
      apiSite.organization?.id || apiSite.organizationId || 0,
    organizationName:
      apiSite.organizationName || apiSite.organization?.name || "",
    flockActive: apiSite.active !== false,
    flockName: apiSite.name || "",
    flockNumber: apiSite.number || "",
    flockStreetAddress: apiSite.streetAddress || "",
    flockCity: apiSite.city || "",
    flockStateId: apiSite.state?.id
      ? typeof apiSite.state.id === "string"
        ? parseInt(apiSite.state.id, 10)
        : apiSite.state.id
      : apiSite.stateId || 10, // Parse string IDs, fallback to Florida (10)
    flockZip: apiSite.zip || "",
    flockCountyId: apiSite.county?.id || apiSite.countyId || 0,
    countyName: apiSite.countyName || apiSite.county?.name || "",
    flockIsAddressValidated: apiSite.isAddressValidated || false,
    flockLatitude: apiSite.latitude || "",
    flockLongitude: apiSite.longitude || "",
    flockGpsNotes: apiSite.gpsNote || "",
    flockComment: apiSite.comment || "",
    flockPrimaryContactFirstName: apiSite.primaryContactFirstName || "",
    flockPrimaryContactLastName: apiSite.primaryContactLastName || "",
    flockPrimaryContactEmail: apiSite.primaryContactEmail || "",
    flockPrimaryContactPhoneNumber: apiSite.primaryContactPhoneNumber || "",
    flockSecondaryContactFirstName: apiSite.secondaryContactFirstName || "",
    flockSecondaryContactLastName: apiSite.secondaryContactLastName || "",
    flockSecondaryContactEmail: apiSite.secondaryContactEmail || "",
    flockSecondaryContactPhoneNumber: apiSite.secondaryContactPhoneNumber || "",
    // Transform birds if requested and available
    flockBirds: includeBirds
      ? (apiSite.birds || []).map((bird: any) => ({
          birdId: bird.id,
          birdFlockId: apiSite.id,
          birdActive: bird.active !== false,
          birdNumber: bird.birdNumber || "",
          birdSpecies: bird.species?.id || 20, // Transform species.id (API) -> birdSpecies
          birdAgeInWeeks: bird.ageInWeeks || 0,
          birdDateFirstBleed: isoToDateOnly(bird.dateOfFirstBleed),
          birdDateTerminated: isoToDateOnly(bird.dateTerminated),
          birdNote: bird.note || "", // Transform note (API) -> birdNote
        }))
      : [],
    birdCount: apiSite.numberOfBirds || apiSite.birds?.length || 0,
    createdBy: apiSite.createdByUserName || "",
    updatedBy: apiSite.modifiedByUserName || "",
    createdUtc: apiSite.dateCreated || new Date().toISOString(),
    updatedUtc: apiSite.dateModified || new Date().toISOString(),
  };
};

// Check if a bird has changed compared to current state
const hasBirdChanged = (currentBird: any, updatedBird: any): boolean => {
  if (!currentBird) return true;

  return (
    currentBird.birdNumber !== updatedBird.birdNumber ||
    currentBird.birdSpecies !== updatedBird.birdSpecies ||
    currentBird.birdAgeInWeeks !== updatedBird.birdAgeInWeeks ||
    currentBird.birdDateFirstBleed !== updatedBird.birdDateFirstBleed ||
    currentBird.birdDateTerminated !== updatedBird.birdDateTerminated ||
    currentBird.birdNote !== updatedBird.birdNote ||
    currentBird.birdActive !== updatedBird.birdActive
  );
};

interface SentinelState {
  flocks: FlockRecord[];
  packingSlips: PackingSlip[]; // Packing slips from API
  activeBirds: Bird[]; // Active birds for packing slip sample selection
  loading: boolean;
  error?: string | null;
  loadAll: () => Promise<void>;
  loadPackingSlips: () => Promise<void>;
  loadActiveBirds: () => Promise<void>;
  loadPackingSlipDetails: (id: number) => Promise<PackingSlip | null>;
  loadSiteDetails: (id: number) => Promise<FlockRecord>;
  add: (input: AddFlockInput) => Promise<FlockRecord>;
  update: (
    id: number,
    input: FlockFormInput,
    options?: {
      onProgress?: (current: number, total: number) => void;
      flockIsDirty?: boolean;
      dirtyBirdIndices?: number[];
    }
  ) => Promise<FlockRecord>;
  addPackingSlip: (input: AddPackingSlipInput) => Promise<PackingSlip>;
  getById: (id: number) => FlockRecord | undefined;
  getPackingSlipById: (id: number) => PackingSlip | undefined;
}

export const useSentinelStore = create<SentinelState>()(
  devtools((set, get) => ({
    flocks: [],
    packingSlips: [],
    activeBirds: [],
    loading: false,
    error: null,
    loadAll: async () => {
      // Always reload data to ensure freshness
      // Note: We don't clear flocks/packingSlips here to allow merge logic to preserve detailed data
      set({
        loading: true,
        error: null,
      });

      try {
        // Try to load from API first if client is available
        if (apiClient) {
          const apiData = await apiClient.authenticatedRequest(async () => {
            const response = await apiClient.api("api/sentinel").get();
            // Extract data from the response.content property
            return response.content;
          });

          // Handle different API response formats
          const flocksArray = Array.isArray(apiData)
            ? apiData
            : apiData?.results || apiData?.data || apiData?.flocks || [];

          // Filter out any invalid data (e.g., lookup data that got mixed in)
          const validFlocks = flocksArray.filter((site: any) => {
            // A valid sentinel site should have these properties
            return (
              site &&
              typeof site === "object" &&
              (site.hasOwnProperty("name") ||
                site.hasOwnProperty("number") ||
                site.hasOwnProperty("streetAddress") ||
                site.hasOwnProperty("organizationName") ||
                site.hasOwnProperty("organization"))
            );
          });

          // Use unified transformation function (no birds in summary endpoint)
          const transformedFlocks: FlockRecord[] = validFlocks.map(
            (site: any) => mapApiFlockToRecord(site, { includeBirds: false })
          );

          // Merge with existing detailed flock data instead of replacing everything
          set((state) => {
            const mergedFlocks = transformedFlocks.map((summaryFlock) => {
              // Find existing flock with detailed data
              const existingFlock = state.flocks.find(
                (f) => f.flockId === summaryFlock.flockId
              );

              // If flock exists with detailed data (has birds or detailed fields loaded),
              // preserve the detailed data and only update summary-level fields
              if (existingFlock && existingFlock.flockBirds.length > 0) {
                return {
                  ...existingFlock, // Keep all detailed data as base
                  // Update only the fields that come from summary endpoint
                  organizationName: summaryFlock.organizationName,
                  countyName: summaryFlock.countyName,
                  flockActive: summaryFlock.flockActive,
                  flockName: summaryFlock.flockName,
                  flockNumber: summaryFlock.flockNumber,
                  birdCount: summaryFlock.birdCount,
                  createdBy: summaryFlock.createdBy,
                  updatedBy: summaryFlock.updatedBy,
                  createdUtc: summaryFlock.createdUtc,
                  updatedUtc: summaryFlock.updatedUtc,
                };
              }

              // Otherwise use the summary data as-is (no detailed data loaded yet)
              return summaryFlock;
            });

            return {
              flocks: mergedFlocks,
              error: null,
            };
          });

          return;
        }

        // Throw error if no API client
        throw new Error("API client not available");
      } catch (apiError: any) {
        console.error("❌ Sentinel API call failed:", apiError);
        const errorMessage =
          apiError instanceof Error
            ? apiError.message
            : "Failed to load sentinel data";

        set({
          error: `API Error: ${errorMessage}. Please check your connection and try again.`,
          loading: false,
        });
      } finally {
        set({ loading: false });
      }
    },

    loadPackingSlips: async () => {
      set({ loading: true, error: null });

      try {
        // Try to load from API
        if (!apiClient) {
          throw new Error("API client not available");
        }

        const packingSlipsData = await apiClient.authenticatedRequest(
          async () => {
            const response = await apiClient
              .api("api/sentinel/packingSlips")
              .get();
            return response.content;
          }
        );

        // Store API response directly - no transformations needed
        const packingSlips = Array.isArray(packingSlipsData)
          ? packingSlipsData
          : [];

        set({
          packingSlips: packingSlips,
          error: null,
          loading: false,
        });
      } catch (apiError: any) {
        console.error("❌ Packing slips API call failed:", apiError);
        const errorMessage =
          apiError instanceof Error
            ? apiError.message
            : "Failed to load packing slips";

        set({
          packingSlips: [],
          error: `API Error: ${errorMessage}`,
          loading: false,
        });
      }
    },

    loadActiveBirds: async () => {
      set({ loading: true, error: null });

      try {
        // Try to load from API
        if (!apiClient) {
          throw new Error("API client not available");
        }

        const birdsData = await apiClient.authenticatedRequest(async () => {
          const response = await apiClient
            .api("api/sentinel/packingSlips/birds")
            .get();
          return response.content;
        });

        // Store API response directly - no transformations needed
        const activeBirds = Array.isArray(birdsData) ? birdsData : [];

        set({
          activeBirds: activeBirds,
          error: null,
          loading: false,
        });
      } catch (apiError: any) {
        console.error("❌ Active birds API call failed:", apiError);

        set({
          activeBirds: [],
          error: `Failed to load active birds: ${apiError.message}`,
          loading: false,
        });
      }
    },

    loadPackingSlipDetails: async (id: number) => {
      try {
        if (apiClient) {
          const packingSlipData = await apiClient.authenticatedRequest(
            async () => {
              const response = await apiClient
                .api(`api/sentinel/packingSlips/${id}`)
                .get();
              return response.content;
            }
          );

          // Store API response directly - no transformations
          const packingSlip: PackingSlip = packingSlipData;

          // Update the specific packing slip in the store with full details
          set((state) => ({
            packingSlips: state.packingSlips.map((slip) =>
              slip.id === id ? packingSlip : slip
            ),
          }));

          return packingSlip;
        }

        // Fallback to existing packing slip data if no API client
        const existingPackingSlip = get().packingSlips.find(
          (ps) => ps.id === id
        );
        if (existingPackingSlip) {
          return existingPackingSlip;
        }

        throw new Error(`Packing slip with ID ${id} not found`);
      } catch (error: any) {
        // Return existing packing slip data if available, otherwise throw
        const existingPackingSlip = get().packingSlips.find(
          (ps) => ps.id === id
        );
        if (existingPackingSlip) {
          return existingPackingSlip;
        }

        throw error;
      }
    },

    loadSiteDetails: async (id: number) => {
      try {
        if (apiClient) {
          const siteData = await apiClient.authenticatedRequest(async () => {
            const response = await apiClient.api(`api/sentinel/${id}`).get();
            return response.content;
          });

          // Use unified transformation function (WITH birds for detail endpoint)
          const transformedSite: FlockRecord = mapApiFlockToRecord(siteData, {
            includeBirds: true,
          });

          // Update the specific flock in the store with full details
          set((state) => {
            const flockIndex = state.flocks.findIndex(
              (flock) => flock.flockId === id
            );

            if (flockIndex >= 0) {
              // Flock exists, update it
              return {
                flocks: state.flocks.map((flock) =>
                  flock.flockId === id ? transformedSite : flock
                ),
              };
            } else {
              // Flock doesn't exist, add it
              return {
                flocks: [...state.flocks, transformedSite],
              };
            }
          });

          return transformedSite;
        }

        // Fallback to existing flock data if no API client
        const existingFlock = get().flocks.find((f) => f.flockId === id);
        if (existingFlock) {
          return existingFlock;
        }

        throw new Error(`Site with ID ${id} not found`);
      } catch (error: any) {
        // Return existing flock data if available, otherwise throw
        const existingFlock = get().flocks.find((f) => f.flockId === id);
        if (existingFlock) {
          return existingFlock;
        }

        throw error;
      }
    },
    add: async (input: AddFlockInput) => {
      if (!apiClient) {
        throw new Error("API client not initialized");
      }

      set({ loading: true, error: null });

      try {
        // Send input directly to API
        const apiResult = await apiClient.api("api/sentinel").post(input);
        const flockResponse = apiResult as any;

        // Use unified transformation function to map API response to FlockRecord
        const newFlock: FlockRecord = mapApiFlockToRecord(flockResponse, {
          includeBirds: false, // New flocks don't have birds in the response yet
        });

        set((state) => ({
          flocks: [...state.flocks, newFlock],
          loading: false,
        }));

        return newFlock;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to add flock";
        set({ loading: false, error: errorMessage });
        throw error;
      }
    },
    update: async (id, input, options) => {
      const {
        onProgress,
        flockIsDirty = true,
        dirtyBirdIndices,
      } = options || {};
      try {
        // Try to update via API
        if (apiClient) {
          const inputBirds = input.flockBirds || [];

          // Get current flock to compare birds
          const currentFlock = get().flocks.find((f) => f.flockId === id);
          const currentBirds = currentFlock?.flockBirds || [];

          // Identify birds to update - filter by dirtyBirdIndices if provided
          const birdsToUpdate = inputBirds
            .filter((bird, index) => {
              if (!bird.birdId) return false;
              // If dirtyBirdIndices provided, only include birds at those indices
              if (
                dirtyBirdIndices !== undefined &&
                !dirtyBirdIndices.includes(index)
              ) {
                return false;
              }
              const currentBird = currentBirds.find(
                (b) => b.birdId === bird.birdId
              );
              return hasBirdChanged(currentBird, bird);
            })
            .map((bird) => transformBirdToApiUpdate(bird));

          // Identify new birds
          const newBirds = inputBirds.filter((bird) => !bird.birdId);

          // Calculate total operations: site (if dirty) + updates + adds
          const totalOperations =
            (flockIsDirty ? 1 : 0) + birdsToUpdate.length + newBirds.length;
          let completedOperations = flockIsDirty ? 0 : 0;

          // Helper to update progress
          const updateProgress = () => {
            completedOperations++;
            if (onProgress) {
              onProgress(completedOperations, totalOperations);
            }
          };

          // 1. Update the site/flock details only if dirty
          if (flockIsDirty) {
            const siteUpdateData = transformFlockToApiUpdate(input, id);

            await apiClient.authenticatedRequest(async () => {
              const response = await apiClient
                .api(`api/sentinel/${id}`)
                .request("PATCH", siteUpdateData);

              // Check if response is an error
              if (!response.ok) {
                throw response;
              }

              return response.content;
            });
            updateProgress();
          }

          // Comment is already included in the flock update above (via transformFlockToApiUpdate)
          // No separate comment endpoint exists - comment is part of UpdateFlockInput

          // 2. Update existing birds (only if they've changed) - INDIVIDUAL UPDATES
          if (birdsToUpdate.length > 0) {
            // Update each bird individually since there's no batch endpoint
            for (const birdData of birdsToUpdate) {
              const birdId = birdData.id;

              await apiClient.authenticatedRequest(async () => {
                const response = await apiClient
                  .api(`api/sentinel/birds/${birdId}`)
                  .request("PATCH", birdData);

                // Check if response is an error
                if (!response.ok) {
                  throw response;
                }

                return response.content;
              });
              updateProgress();
            }
          }

          // 3. Add new birds (those without birdId)
          for (const bird of newBirds) {
            const birdAddData = {
              birdNumber: bird.birdNumber || "",
              speciesId: bird.birdSpecies || 20, // Transform birdSpecies -> speciesId (API)
              ageInWeeks: bird.birdAgeInWeeks || 0,
              dateOfFirstBleed:
                dateToISOString(bird.birdDateFirstBleed) ||
                new Date().toISOString(),
              dateTerminated: dateToISOString(bird.birdDateTerminated),
              note: bird.birdNote || "", // Transform birdNote -> note (API)
              active: bird.birdActive !== false,
            };

            await apiClient.authenticatedRequest(async () => {
              const response = await apiClient
                .api(`api/sentinel/${id}/birds`)
                .request("POST", birdAddData);

              // Check if response is an error
              if (!response.ok) {
                // =============================================================
                // TEMPORARY WORKAROUND: @fdoh/api-client discards error bodies
                // TODO: Remove this block once api-client exposes error content
                // =============================================================
                if (response.status === 400) {
                  const birdNum = bird.birdNumber || "unknown";
                  throw {
                    status: 400,
                    detail: `A bird with number '${birdNum}' already exists in this flock. Bird numbers must be unique.`,
                  };
                }
                // =============================================================
                // END TEMPORARY WORKAROUND
                // =============================================================
                throw response;
              }

              return response.content;
            });
            updateProgress();
          }

          // Reload the flock to sync with database state
          // This ensures UI reflects the latest data after updates
          const reloadedFlock = await get().loadSiteDetails(id);

          // Return the reloaded flock
          return reloadedFlock;
        }
      } catch (apiError) {
        throw apiError;
      }

      // Return the updated flock from state
      const updated = get().flocks.find((f) => f.flockId === id);
      if (!updated) throw new Error("Not found");
      return updated;
    },
    getById: (id) => get().flocks.find((x) => x.flockId === id),

    addPackingSlip: async (input: AddPackingSlipInput) => {
      try {
        // Try to add via API
        if (!apiClient) {
          throw new Error("API client not available");
        }

        const apiResult = await apiClient.authenticatedRequest(async () => {
          const response = await apiClient
            .api("api/sentinel/packingSlips")
            .post(input); // Send input directly - it already matches API structure
          return response.content;
        });

        // Store API response directly - it's already a PackingSlip
        const packingSlip: PackingSlip = apiResult;

        set((s) => ({
          packingSlips: [...s.packingSlips, packingSlip],
        }));

        return packingSlip;
      } catch (apiError: any) {
        throw new Error(`Failed to create packing slip: ${apiError.message}`);
      }
    },

    getPackingSlipById: (id) => {
      return get().packingSlips.find((x) => x.id === id);
    },
  }))
);
